<template>
    <div
        id="stockcountpage"
        class="max-w-[calc(100vw_-_40px)] overflow-x-hidden"
    >
        <actions-menu>
            <actions-menu-item
                :v4="true"
                :action="() => $router.push('/addprofilepage')"
                v-if="hasRight('productionmanagement')"
                icon="list"
                :label="trans('pages.stockcount.menu_items.new_profile')"
            />
            <actions-menu-item
                :v4="true"
                :action="() => $router.push('/addloonlakwerkpage')"
                v-if="hasRight('productionmanagement')"
                icon="list"
                :label="trans('pages.stockcount.menu_items.new_loonlakwerk')"
            />
            <actions-menu-item
                :v4="true"
                :action="createNewStockCount"
                :disabled="newstockcountDisabled"
                v-if="hasRight('productionmanagement')"
                icon="list"
                :label="trans('pages.stockcount.menu_items.new_count')"
            />
            <actions-menu-item
                :v4="true"
                :action="exportStockCount"
                v-if="hasRight('administration')"
                icon="download"
                :label="trans('pages.stockcount.menu_items.export_count')"
            />
            <actions-menu-item
                :v4="true"
                :action="exportProfiles"
                v-if="hasRight('administration')"
                icon="download"
                :label="trans('pages.stockcount.menu_items.export_profiles')"
            />
            <actions-menu-item
                :action="exportFilters"
                icon="file-pdf"
                :label="
                    trans(
                        'pages.stockcount.menu_items.export_filters_without_photo'
                    )
                "
            />
            <actions-menu-item
                :action="exportFiltersPhotos"
                icon="file-pdf"
                :label="
                    trans(
                        'pages.stockcount.menu_items.export_filters_with_photo'
                    )
                "
            />
            <actions-menu-item
                :action="exportGeneral"
                icon="file-pdf"
                :label="
                    trans('pages.stockcount.menu_items.export_without_photo')
                "
            />
            <actions-menu-item
                :action="exportPhotos"
                icon="file-pdf"
                :label="trans('pages.stockcount.menu_items.export_with_photo')"
            />
            <actions-menu-item
                :action="exportGrouped"
                icon="file-pdf"
                :label="trans('pages.stockcount.menu_items.export_grouped')"
            />
            <actions-menu-item
                :action="exportToOrder"
                icon="file-pdf"
                :label="trans('pages.stockcount.menu_items.export_to_order')"
            />
            <actions-menu-item
                :action="exportPricelist"
                icon="file-pdf"
                :label="trans('pages.stockcount.menu_items.export_pricelist')"
            />
            <actions-menu-item
                :v4="true"
                :action="printKanbanWithFilter"
                icon="print"
                :label="
                    trans(
                        'pages.stockcount.menu_items.print_kanban_with_filter'
                    )
                "
            />
            <actions-menu-item
                :v4="true"
                :action="exportImages"
                icon="file-archive-o"
                :label="
                    trans(
                        'pages.stockcount.menu_items.export_stock_item_images'
                    )
                "
            />
        </actions-menu>

        <div class="button-row">
            <ui5-button @click="switchStockcount()">
                {{ switchStockcountButtonLabel }}
            </ui5-button>

            <ui5-button @click="switchStockcountPhotos()">
                {{ switchStockcountPhotosButtonLabel }}
            </ui5-button>

            <ui5-button @click="$router.push('/stockcounttablet')">
                {{ trans('pages.stockcount.buttons.tablet') }}
            </ui5-button>

            <ui5-button
                class="right-side"
                :disabled="workstation === null"
                @click="inventoryMode = !inventoryMode"
            >
                <template v-if="inventoryMode">
                    {{ trans('pages.stockcount.buttons.inventory_finish') }}
                </template>
                <template v-else>
                    {{ trans('pages.stockcount.buttons.inventory_compose') }}
                </template>
            </ui5-button>

            <ui5-select
                class="select right-side"
                :disabled="inventoryMode === true"
                :value="workstation"
                @change="workstation = $event.target.selectedOption.value"
            >
                <ui5-option
                    v-for="option in workstationOptions"
                    :value="option.value"
                    :key="option.value"
                >
                    {{ option.label }}
                </ui5-option>
            </ui5-select>
        </div>

        <div class="explanation">
            <p>{{ trans('pages.stockcount.explanation') }}</p>
        </div>

        <div class="overflow-x-auto uses-tailwind">
            <table class="table-auto w-full border-collapse overflow-visible">
                <thead>
                    <tr class="text-nowrap">
                        <th class="px-1 py-2">
                            {{ trans('pages.stockcount.columns.actions') }}
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="showStockcountPhotos"
                        >
                            {{ trans('pages.stockcount.columns.photo') }}
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            {{ trans('pages.stockcount.columns.count') }}
                        </th>
                        <th class="px-1 py-2">
                            <ui5-input
                                class="w-full min-w-[50px]"
                                :placeholder="
                                    trans('pages.stockcount.columns.rack')
                                "
                                :value="locationFilter"
                                @input="locationFilter = $event.target.value"
                                v-on:keyup.enter="
                                    populateStockCountOverviewWithReset
                                "
                            />
                        </th>
                        <th class="px-1 py-2">
                            <ui5-input
                                class="w-full min-w-[150px]"
                                :placeholder="
                                    trans(
                                        'pages.stockcount.columns.article_number'
                                    )
                                "
                                :value="codeFilter"
                                @input="codeFilter = $event.target.value"
                                v-on:keyup.enter="
                                    populateStockCountOverviewWithReset
                                "
                            />
                        </th>
                        <th class="px-1 py-2">
                            <ui5-input
                                class="w-full min-w-[150px]"
                                :placeholder="
                                    trans(
                                        'pages.stockcount.columns.description'
                                    )
                                "
                                :value="profileDescriptionFilter"
                                @input="
                                    profileDescriptionFilter =
                                        $event.target.value
                                "
                                v-on:keyup.enter="
                                    populateStockCountOverviewWithReset
                                "
                            />
                        </th>
                        <th class="px-1 py-2">
                            <ui5-input
                                class="w-full min-w-[150px]"
                                :placeholder="
                                    trans(
                                        'pages.stockcount.columns.supplier_article_number'
                                    )
                                "
                                :value="profileSupplierCodeFilter"
                                @input="
                                    profileSupplierCodeFilter =
                                        $event.target.value
                                "
                                v-on:keyup.enter="
                                    populateStockCountOverviewWithReset
                                "
                            />
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isDefaultView"
                        >
                            <auto-complete-input
                                class="w-full min-w-[150px]"
                                v-if="profileSupplierOptions"
                                v-model="profileSuppliersFilter"
                                :initial-search="
                                    getLabel(
                                        profileSuppliersFilter,
                                        profileSupplierOptions
                                    )
                                "
                                :options="profileSupplierOptions"
                                :placeholder="
                                    trans('pages.stockcount.columns.supplier')
                                "
                            />
                        </th>
                        <th class="px-1 py-2">
                            <auto-complete-input
                                class="w-full min-w-[150px]"
                                v-if="filterGroupOptions"
                                v-model="filterGroupsFilter"
                                :initial-search="
                                    getLabel(
                                        filterGroupsFilter,
                                        filterGroupOptions
                                    )
                                "
                                :options="filterGroupOptions"
                                :placeholder="
                                    trans('pages.stockcount.columns.group')
                                "
                            />
                        </th>
                        <th class="px-1 py-2">
                            <auto-complete-input
                                class="w-full min-w-[150px]"
                                v-if="profileSubGroupOptions"
                                v-model="profileSubGroupsFilter"
                                :initial-search="
                                    getLabel(
                                        profileSubGroupsFilter,
                                        profileSubGroupOptions
                                    )
                                "
                                :options="profileSubGroupOptions"
                                :placeholder="
                                    trans('pages.stockcount.columns.subgroup')
                                "
                            />
                        </th>
                        <th class="px-1 py-2">
                            {{ trans('pages.stockcount.columns.unit') }}
                        </th>
                        <th class="px-1 py-2">
                            {{
                                trans(
                                    'pages.stockcount.columns.package_per_unit'
                                )
                            }}
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            {{ trans('pages.stockcount.columns.weight') }}
                        </th>
                        <th class="px-1 py-2">
                            <ui5-select
                                class="select w-full min-w-[100px]"
                                :value="stockFilter"
                                @change="
                                    stockFilter =
                                        $event.target.selectedOption.value
                                "
                            >
                                <ui5-option
                                    value="-1"
                                    selected="selected"
                                >
                                    {{
                                        trans(
                                            'pages.stockcount.filters.all_stock'
                                        )
                                    }}
                                </ui5-option>
                                <ui5-option value="1">
                                    {{
                                        trans(
                                            'pages.stockcount.filters.stock_yes'
                                        )
                                    }}
                                </ui5-option>
                                <ui5-option value="0">
                                    {{
                                        trans(
                                            'pages.stockcount.filters.stock_no'
                                        )
                                    }}
                                </ui5-option>
                            </ui5-select>
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isDefaultView"
                        >
                            <ui5-select
                                class="select w-full min-w-[100px]"
                                :value="priceListFilter"
                                @change="
                                    priceListFilter =
                                        $event.target.selectedOption.value
                                "
                            >
                                <ui5-option
                                    value=""
                                    selected="selected"
                                >
                                    {{
                                        trans(
                                            'pages.stockcount.filters.pricelist_all'
                                        )
                                    }}
                                </ui5-option>
                                <ui5-option value="1">
                                    {{
                                        trans(
                                            'pages.stockcount.filters.pricelist_yes'
                                        )
                                    }}
                                </ui5-option>
                                <ui5-option value="0">
                                    {{
                                        trans(
                                            'pages.stockcount.filters.pricelist_no'
                                        )
                                    }}
                                </ui5-option>
                            </ui5-select>
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            {{ trans('pages.stockcount.columns.min_stock') }}
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            {{ trans('pages.stockcount.columns.max_stock') }}
                        </th>
                        <th class="px-1 py-2">
                            {{ trans('pages.stockcount.columns.total_stock') }}
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            {{
                                trans('pages.stockcount.columns.current_stock')
                            }}
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            {{ trans('pages.stockcount.columns.spare_stock') }}
                        </th>
                        <th
                            class="px-1 py-2"
                            v-show="isDefaultView"
                        >
                            {{
                                trans('pages.stockcount.columns.reserved_stock')
                            }}
                        </th>
                        <th class="px-1 py-2">
                            <i
                                @click="populateStockCountOverviewWithReset()"
                                class="fa fa-filter fa-2x"
                            />
                            <i
                                @click="clearStockCountFilters()"
                                class="fa fa-ban fa-2x"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="item in sortedList"
                        :class="{
                            'bg-primary/40':
                                inventoryMode && hasWorkstationInventory(item),
                        }"
                    >
                        <td class="px-1 py-2">
                            <div class="flex items-center gap-1">
                                <button
                                    @click="backToProfilePage(item.profileId)"
                                    class="cursor-pointer"
                                >
                                    <i class="fa fa-info-circle"></i>
                                </button>
                                <button
                                    @click="copyProfile(item.profileId)"
                                    class="cursor-pointer"
                                >
                                    <i class="fa fa-copy"></i>
                                </button>
                                <button
                                    class="cursor-pointer"
                                    @click="
                                        createOrderline(
                                            item.profileId,
                                            item.code
                                        )
                                    "
                                >
                                    <i class="fa fa-cart-arrow-down"></i>
                                </button>
                                <button
                                    class="cursor-pointer"
                                    :title="
                                        trans(
                                            'pages.stockcount.actions.add_to_inventory'
                                        )
                                    "
                                    v-show="
                                        inventoryMode &&
                                        !hasWorkstationInventory(item)
                                    "
                                    @click="createWorkstationInventory(item)"
                                >
                                    <i class="fa fa-plus"></i>
                                </button>
                                <button
                                    class="cursor-pointer"
                                    :title="
                                        trans(
                                            'pages.stockcount.actions.remove_from_inventory'
                                        )
                                    "
                                    v-show="
                                        inventoryMode &&
                                        hasWorkstationInventory(item)
                                    "
                                    @click="deleteWorkstationInventory(item)"
                                >
                                    <i class="fa fa-minus"></i>
                                </button>
                            </div>
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="showStockcountPhotos"
                        >
                            <img
                                class="h-[100px] aspect-square object-contain object-center max-w-none"
                                v-if="item.cloudFrontPath"
                                :src="item.cloudFrontPath"
                            />
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            <ui5-input
                                type="number"
                                :class="item.profilecountsequenceClass"
                                :value="item.countsequence"
                                @input="
                                    item.countsequence = $event.target.value
                                "
                                @blur="updateProfileCountSequence(item)"
                            />
                        </td>
                        <td class="px-1 py-2 text-center">
                            {{ item.location }}
                        </td>
                        <td class="px-1 py-2">
                            {{ item.code }}
                        </td>
                        <td class="px-1 py-2">
                            {{ item.description }}
                        </td>
                        <td class="px-1 py-2">
                            {{ item.supplierProfileCode }}
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="isDefaultView"
                        >
                            {{ item.supplierName }}
                        </td>
                        <td class="px-1 py-2">
                            {{ item.groupName }}
                        </td>
                        <td class="px-1 py-2">
                            {{ item.subgroupName }}
                        </td>
                        <td class="px-1 py-2">
                            <ui5-select
                                v-show="isStockcountView"
                                class="select w-full min-w-[100px]"
                                @change="
                                    updateProfileUnit(
                                        item,
                                        $event.target.selectedOption.value
                                    )
                                "
                            >
                                <ui5-option
                                    v-for="option in profileUnitOptions"
                                    :value="option.id"
                                    :key="
                                        'profile-unit-option-' +
                                        parseInt(option.id)
                                    "
                                    :selected="option.id == item.unitRef"
                                >
                                    {{ option.name }}
                                </ui5-option>
                            </ui5-select>
                            <p v-show="isDefaultView">
                                {{ item.unitName }}
                            </p>
                        </td>
                        <td class="px-1 py-2">
                            {{ item.packagePerUnit }}
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            <ui5-input
                                type="number"
                                :class="item.currentprofilestockClass"
                                :value="item.profileStock"
                                @input="item.profileStock = $event.target.value"
                                @blur="updateProfileStockWithWeight(item)"
                            />
                        </td>
                        <td class="px-1 py-2">
                            <ui5-switch
                                :text-on="trans('pages.stockcount.toggle.yes')"
                                :text-off="trans('pages.stockcount.toggle.no')"
                                :checked="item.keepStock == '1' ? true : false"
                                @change="
                                    item.keepStock = $event.target.checked
                                        ? '1'
                                        : '0';
                                    updateProfileKeepStock(item);
                                "
                            />
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="isDefaultView"
                        >
                            <ui5-switch
                                :text-on="trans('pages.stockcount.toggle.yes')"
                                :text-off="trans('pages.stockcount.toggle.no')"
                                :checked="item.pricelist == '1' ? true : false"
                                @change="
                                    item.pricelist = $event.target.checked
                                        ? '1'
                                        : '0';
                                    updateProfilePriceList(item);
                                "
                            />
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            <ui5-input
                                type="text"
                                :class="item.currentprofileminstockClass"
                                :value="item.minStock"
                                @input="item.minStock = $event.target.value"
                                @blur="updateProfileMinStock(item)"
                            />
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            <ui5-input
                                type="text"
                                :class="item.currentprofilemaxstockClass"
                                :value="item.maxStock"
                                @input="item.maxStock = $event.target.value"
                                @blur="updateProfileMaxStock(item)"
                            />
                        </td>
                        <td class="px-1 py-2 text-center">
                            {{
                                precisionRound(totalStockCountForItem(item), 2)
                            }}
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            <ui5-input
                                type="text"
                                :class="item.currentprofilestockClass"
                                :value="item.currentstockcount"
                                @input="
                                    item.currentstockcount = $event.target.value
                                "
                                @blur="updateProfileStock(item)"
                            />
                        </td>
                        <td
                            class="px-1 py-2"
                            v-show="isStockcountView"
                        >
                            <ui5-input
                                type="text"
                                :class="item.spareStockClass"
                                :value="item.spareStock"
                                @input="item.spareStock = $event.target.value"
                                @blur="updateProfileSpareStock(item)"
                            />
                        </td>
                        <td
                            class="px-1 py-2 text-center"
                            v-show="isDefaultView"
                        >
                            {{ precisionRound(item.currentreservedcount, 2) }}
                        </td>
                        <td class="px-1 py-2">
                            <!-- Filter actions -->
                        </td>
                    </tr>
                </tbody>
            </table>
            <div
                v-if="profilePageLoading"
                class="w-full flex items-center justify-center px-1 py-2"
            >
                <spinner></spinner>
            </div>
            <div
                v-if="profileAtBottom"
                class="w-full flex items-center justify-center px-1 py-2"
            >
                {{ trans('pages.stockcount.messages.no_products_found') }}
            </div>
        </div>

        <add-order-line-dialog
            ref="addOrderLineDialog"
            :profile-id-param="popupProfileId"
        ></add-order-line-dialog>
    </div>
</template>
<script>
import profileApi from '../api/profile';
import profileUnitApi from '../api/profileUnit';
import supplierApi from '../api/supplier';
import exportApi from '../api/export';
import workstationApi from '../api/workstation';
import { mapFields } from 'vuex-map-fields';
import spinner from '../components/loader/Spinner';
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import { mapGetters } from 'vuex';
import { isNumber } from '../helpers/GeneralHelper';
import { precisionRound } from '../helpers/MathHelper';
import { showInfoMessage } from '../dialogs';
import _ from 'lodash';
import api from '../api/api';
import AddOrderLineDialog from '../components/orderLines/AddOrderLineDialog';
import AutoCompleteInput from '../components/AutoCompleteInput';
import { getLabel } from '../shared/collections';
import localization from '../mixins/localization';

export default {
    components: {
        AddOrderLineDialog,
        spinner,
        ActionsMenu,
        ActionsMenuItem,
        AutoCompleteInput,
    },
    mixins: [localization],
    data() {
        return {
            profileUnitOptions: [],
            popupProfileId: null,
            inventoryMode: false,
            workstation: null,
            stockcountlist: [],
            profilePage: 0,
            profileAtBottom: false,
            profilePageLoading: true,
            // Buttons
            newstockcountDisabled: false,
            // Options
            workstationOptions: [],
            filterGroupOptions: [],
            profileSubGroupOptions: [],
            profileSupplierOptions: [],
            // Button labels
            switchStockcountButtonLabel: this.trans(
                'pages.stockcount.buttons.stockcount_overview'
            ),
            switchStockcountPhotosButtonLabel: this.trans(
                'pages.stockcount.buttons.show_photos'
            ),
            // Config
            Config,
        };
    },
    computed: {
        ...mapGetters({
            hasRight: 'currentUser/hasRight',
        }),
        ...mapFields({
            currentStockcountOverview: 'stockcount.currentStockcountOverview',
            showStockcountPhotos: 'stockcount.showStockcountPhotos',
            // Filters
            codeFilter: 'stockcount.filters.code',
            locationFilter: 'stockcount.filters.location',
            profileSupplierCodeFilter: 'stockcount.filters.profileSupplierCode',
            profileDescriptionFilter: 'stockcount.filters.profileDescription',
            stockFilter: 'stockcount.filters.stock',
            priceListFilter: 'stockcount.filters.priceList',
            filterGroupsFilter: 'stockcount.filters.filterGroups',
            profileSubGroupsFilter: 'stockcount.filters.profileSubGroups',
            profileSuppliersFilter: 'stockcount.filters.profileSuppliers',
        }),
        isDefaultView() {
            return this.currentStockcountOverview === 'default';
        },
        isStockcountView() {
            return this.currentStockcountOverview === 'stockcount';
        },
        selectedPrinterPostParameter() {
            return this.$store.getters[
                'printers/getSelectedPrinterPostParameter'
            ]();
        },
        sortedList() {
            const list = [...this.stockcountlist];
            const inventoryMode = this.inventoryMode;

            const listWithInventoryInfo = list.map((item) => ({
                ...item,
                inList: this.hasWorkstationInventory(item),
            }));

            return listWithInventoryInfo.sort((a, b) => {
                if (inventoryMode) {
                    const aSort = !a.inList ? 1 : 0;
                    const bSort = !b.inList ? 1 : 0;

                    if (aSort !== bSort) {
                        return aSort - bSort;
                    }
                }

                return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true });
            });
        },
    },
    methods: {
        getLabel,
        populateStockCountFilters() {
            // Groups
            profileApi.fetchAllProfileGroups().then((response) => {
                this.filterGroupOptions = [];
                this.filterGroupOptions.push({
                    value: 0,
                    label: this.trans('pages.stockcount.filters.all_groups'),
                });

                const groupsArray = response.data.data;
                if (groupsArray.length > 0) {
                    for (let i = 0; i < groupsArray.length; i++) {
                        this.filterGroupOptions.push({
                            value: groupsArray[i].profileGroupId,
                            label: groupsArray[i].name,
                        });
                    }
                }

                // Subgroups
                profileApi.fetchAllProfileSubGroups().then((response) => {
                    this.profileSubGroupOptions = [];
                    this.profileSubGroupOptions.push({
                        value: 0,
                        label: this.trans(
                            'pages.stockcount.filters.all_subgroups'
                        ),
                    });

                    const groupsArray = response.data.data;
                    if (groupsArray.length > 0) {
                        for (let i = 0; i < groupsArray.length; i++) {
                            this.profileSubGroupOptions.push({
                                value: groupsArray[i].profileGroupId,
                                label: groupsArray[i].name,
                            });
                        }
                    }

                    // Suppliers
                    supplierApi.fetchAllSuppliers().then((response) => {
                        this.profileSupplierOptions = [];
                        this.profileSupplierOptions.push({
                            value: 0,
                            label: this.trans(
                                'pages.stockcount.filters.all_suppliers'
                            ),
                        });

                        const suppliersArray = response.data.data;
                        if (suppliersArray.length > 0) {
                            for (let i = 0; i < suppliersArray.length; i++) {
                                this.profileSupplierOptions.push({
                                    value: suppliersArray[i].id,
                                    label: suppliersArray[i].name,
                                });
                            }

                            // load content
                            // Start over with first page
                            this.populateStockCountOverviewWithReset();
                        }
                    });
                });
            }, null);
        },
        populateStockCountOverviewWithReset() {
            this.profileAtBottom = false;
            this.profilePage = 0;

            this.loading = true;

            this.populateStockCountOverview();
        },
        populateStockCountOverview() {
            const payLoad = {};
            payLoad.stockgroup = this.filterGroupsFilter;
            payLoad.subgroup = this.profileSubGroupsFilter;
            payLoad.code = this.codeFilter;
            payLoad.location = this.locationFilter;
            payLoad.profilesuppliercode = this.profileSupplierCodeFilter;
            payLoad.description = this.profileDescriptionFilter;
            payLoad.supplier = this.profileSuppliersFilter;
            payLoad.stock = this.stockFilter;
            payLoad.pricelist = this.priceListFilter;
            payLoad.page = this.profilePage;

            profileApi
                .fetchProfilesWithStock(payLoad)
                .then((response) => {
                    if (this.profilePage === 0) {
                        this.stockcountlist = [];
                    }

                    const profileArray = response.data.data;
                    if (profileArray.length > 0) {
                        const newArray =
                            this.stockcountlist.concat(profileArray);
                        newArray.forEach((x, i) => {
                            x.serverIndex = i;
                        });
                        this.stockcountlist = newArray;
                    } else {
                        // At bottom of page
                        this.profilePageLoading = false;
                        this.profileAtBottom = true;
                    }
                })
                .finally(() => {
                    // Allow loading next page
                    this.profilePageLoading = false;
                });
        },
        populateStockNextPage() {
            if (this.profilePageLoading || this.profileAtBottom) return;

            this.profilePageLoading = true;
            this.profilePage++;
            this.populateStockCountOverview();
        },
        backToProfilePage(prId) {
            this.$router.push('/profilepage/' + prId);
        },
        copyProfile(prId) {
            this.$router.push('/copyprofilepage?copy=' + prId);
        },
        createOrderline(prId) {
            this.popupProfileId = prId;
            this.$refs.addOrderLineDialog.showPopup();
        },
        updateProfileCountSequence(item) {
            const val = item.countsequence;

            item.profilecountsequenceClass = null;

            if (val !== '' && isNumber(val)) {
                const payLoad = {};
                payLoad.profileId = item.profileId;
                payLoad.countsequence = val;

                profileApi
                    .updateProfileCountSequence(payLoad)
                    .then(() => {
                        item.profilecountsequenceClass = 'inputsuccess';
                    })
                    .catch(() => {
                        item.profilecountsequenceClass = 'inputerror';
                    });
            } else {
                item.profilecountsequenceClass = 'inputerror';
            }
        },
        updateProfileCountLocation(item) {
            const val = item.location;

            item.profilecountlocationClass = null;

            if (val !== '') {
                const payLoad = {};
                payLoad.profileId = item.profileId;
                payLoad.location = val;

                profileApi
                    .updateProfileCountLocation(payLoad)
                    .then(() => {
                        item.profilecountlocationClass = 'inputsuccess';
                    })
                    .catch(() => {
                        item.profilecountlocationClass = 'inputerror';
                    });
            } else {
                item.profilecountlocationClass = 'inputError';
            }
        },
        updateProfileStockWithWeight(item) {
            const val = item.profileStock;

            item.currentprofilestockClass = null;

            if (val !== '' && !isNaN(val) && val > 0) {
                // Count is weight in grams minus weight of box in grams, converted to Kg multiplied with the countPerKg
                const count = Math.max(
                    0,
                    ((parseFloat(val) - parseFloat(item.weightBox)) / 1000) *
                        item.countPerKg
                );

                item.profileStock = count;

                const payLoad = {};
                payLoad.profileId = item.profileId;
                payLoad.profileStock = count;

                profileApi
                    .updateProfileStock(payLoad)
                    .then(() => {
                        item.currentprofilestockClass = 'inputsuccess';
                    })
                    .catch(() => {
                        item.currentprofilestockClass = 'inputerror';
                    });
            } else if (val !== '' && !isNaN(val) && val == 0) {
                //Do nothing when user entered value 0
            } else {
                item.profileStock = 0;

                //show error sign
                item.currentprofilestockClass = 'inputError';
            }
        },
        updateProfileKeepStock(item) {
            const val = item.keepStock;

            const payLoad = {};
            payLoad.profileId = item.profileId;
            payLoad.keepStock = val;

            profileApi.updateProfileKeepStock(payLoad);
        },
        updateProfilePriceList(item) {
            const val = item.pricelist;

            const payLoad = {};
            payLoad.profileId = item.profileId;
            payLoad.priceList = val;

            profileApi.updateProfilePriceList(payLoad);
        },
        updateProfileMinStock(item) {
            const val = item.minStock;

            item.currentprofileminstockClass = null;

            if (val !== '' && !isNaN(val)) {
                const payLoad = {};
                payLoad.profileId = item.profileId;
                payLoad.minStock = val;

                profileApi
                    .updateProfileMinStock(payLoad)
                    .then(() => {
                        item.currentprofileminstockClass = 'inputsuccess';
                    })
                    .catch(() => {
                        item.currentprofileminstockClass = 'inputerror';
                    });
            } else {
                item.minStock = 0;
                item.currentprofileminstockClass = 'inputerror';
            }
        },
        updateProfileMaxStock(item) {
            const val = item.maxStock;

            item.currentprofilemaxstockClass = null;

            if (val !== '' && !isNaN(val)) {
                const payLoad = {};
                payLoad.profileId = item.profileId;
                payLoad.maxStock = val;

                profileApi
                    .updateProfileMaxStock(payLoad)
                    .then(() => {
                        item.currentprofilemaxstockClass = 'inputsuccess';
                    })
                    .catch(() => {
                        item.currentprofilemaxstockClass = 'inputerror';
                    });
            } else {
                item.maxStock = 0;
                item.currentprofilemaxstockClass = 'inputerror';
            }
        },
        updateProfileStock(item) {
            const val = item.currentstockcount;

            item.currentprofilestockClass = null;

            if (val !== '' && !isNaN(val)) {
                const payLoad = {};
                payLoad.profileId = item.profileId;
                payLoad.profileStock = val;

                profileApi
                    .updateProfileStock(payLoad)
                    .then(() => {
                        item.currentprofilestockClass = 'inputsuccess';
                    })
                    .catch(() => {
                        item.currentprofilestockClass = 'inputerror';
                    });
            } else {
                item.currentstockcount = 0;
                item.currentprofilestockClass = 'inputerror';
            }
        },
        updateProfileSpareStock(item) {
            const val = item.spareStock;

            item.spareStockClass = null;

            if (val !== '' && !isNaN(val)) {
                const payLoad = {};
                payLoad.profileId = item.profileId;
                payLoad.spareStock = val;

                profileApi
                    .updateProfileSpareStock(payLoad)
                    .then(() => {
                        item.spareStockClass = 'inputsuccess';
                    })
                    .catch(() => {
                        item.spareStockClass = 'inputerror';
                    });
            } else {
                item.spareStock = 0;
                item.spareStockClass = 'inputerror';
            }
        },
        clearStockCountFilters() {
            this.codeFilter = '';
            this.locationFilter = '';
            this.profileSupplierCodeFilter = '';
            this.profileDescriptionFilter = '';

            this.filterGroupsFilter = '0';
            this.profileSubGroupsFilter = '0';
            this.profileSuppliersFilter = '0';
            this.stockFilter = '-1';
            this.priceListFilter = '';

            // Start over with next page
            this.populateStockCountOverviewWithReset();
        },
        createNewStockCount() {
            this.newstockcountDisabled = true;

            const payLoad = {};
            payLoad.stockgroup = this.filterGroupsFilter;

            profileApi
                .insertStockCount(payLoad)
                .then(() => {
                    // Reload view
                    this.populateStockCountOverviewWithReset();
                    showInfoMessage(
                        this.trans(
                            'pages.stockcount.messages.new_count_created'
                        )
                    );
                    this.newstockcountDisabled = false;
                })
                .catch(() => {
                    showInfoMessage(
                        this.trans('pages.stockcount.messages.new_count_failed')
                    );
                    this.newstockcountDisabled = false;
                });
        },
        exportStockCount() {
            const url = profileApi.exportProfileStock();
            window.open(url);
        },
        exportProfiles() {
            const url = profileApi.export();
            window.open(url);
        },
        getFilterPayLoadForGetUrl() {
            const payload = this.getFilterPayload();
            Object.keys(payload).forEach((key) => {
                const value = payload[key];
                if (!value && (value !== 0 || value !== '0')) {
                    payload[key] = '';
                }
            });
            return payload;
        },
        exportFilters() {
            const params = {
                showimages: 0,
                ...this.getFilterPayLoadForGetUrl(),
            };
            window.open(
                api.createGetUrl('/export/exportStockcount/', params),
                '_blank'
            );
        },
        exportFiltersPhotos() {
            const params = {
                showimages: 1,
                ...this.getFilterPayLoadForGetUrl(),
            };
            window.open(
                api.createGetUrl('/export/exportStockcount/', params),
                '_blank'
            );
        },
        exportGeneral() {
            window.open(
                api.createGetUrl('/export/exportStockcount/', {
                    showimages: 0,
                }),
                '_blank'
            );
        },
        exportPhotos() {
            window.open(
                api.createGetUrl('/export/exportStockcount/', {
                    showimages: 1,
                }),
                '_blank'
            );
        },
        exportGrouped() {
            window.open(
                api.createGetUrl('/export/exportStockcountByGroups/'),
                '_blank'
            );
        },
        exportToOrder() {
            window.open(
                api.createGetUrl('/export/exportStockcountToOrder/'),
                '_blank'
            );
        },
        exportPricelist() {
            window.open(
                api.createGetUrl('/export/exportPricelist/', { showimages: 1 }),
                '_blank'
            );
        },
        printKanbanWithFilter() {
            exportApi
                .printKanbanWithFilter(this.getFilterPayload())
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.stockcount.messages.labels_printed')
                    );
                    this.$router.go();
                });
        },
        exportImages() {
            const payload = this.getFilterPayload();
            Object.keys(payload).forEach((key) => {
                payload[key] = payload[key] || '';
            });
            window.open(exportApi.exportStockProfileImages(payload));
        },
        switchStockcount() {
            if (this.currentStockcountOverview == 'stockcount') {
                this.currentStockcountOverview = 'default';
                this.switchStockcountButtonLabel = this.trans(
                    'pages.stockcount.buttons.stockcount_overview'
                );
            } else {
                this.currentStockcountOverview = 'stockcount';
                this.switchStockcountButtonLabel = this.trans(
                    'pages.stockcount.buttons.standard_overview'
                );
            }

            // Reset filter and page
            this.profilePage = 0;
            this.clearStockCountFilters();
        },
        switchStockcountPhotos() {
            if (this.showStockcountPhotos) {
                this.showStockcountPhotos = false;
                this.switchStockcountPhotosButtonLabel = this.trans(
                    'pages.stockcount.buttons.show_photos'
                );
            } else {
                this.showStockcountPhotos = true;
                this.switchStockcountPhotosButtonLabel = this.trans(
                    'pages.stockcount.buttons.hide_photos'
                );
            }
        },
        getFilterPayload() {
            return {
                stockgroup: this.filterGroupsFilter,
                subgroup: this.profileSubGroupsFilter,
                code: this.codeFilter,
                location: this.locationFilter,
                profilesuppliercode: this.profileSupplierCodeFilter,
                description: this.profileDescriptionFilter,
                supplier: this.profileSuppliersFilter,
                stock: this.stockFilter,
                preferredPrinter: this.selectedPrinterPostParameter,
                workstation: this.workstation,
            };
        },
        hasWorkstationInventory(item) {
            const workstationInventory = item.workstationInventories.find(
                (inventory) => inventory.workstationId === this.workstation
            );
            return workstationInventory !== undefined;
        },
        createWorkstationInventory(item) {
            const workstationInventory = {
                profileId: item.profileId,
                workstationId: this.workstation,
                amount: 0,
            };

            profileApi
                .insertWorkstationInventory(workstationInventory)
                .then(() => {
                    item.workstationInventories.push(workstationInventory);
                });
        },
        deleteWorkstationInventory(item) {
            const workstationInventory = {
                profileId: item.profileId,
                workstationId: this.workstation,
            };

            profileApi
                .removeWorkstationInventory(workstationInventory)
                .then(() => {
                    item.workstationInventories =
                        item.workstationInventories.filter(
                            (inventory) =>
                                inventory.workstationId !=
                                workstationInventory.workstationId
                        );
                });
        },
        precisionRound,
        totalStockCountForItem(item) {
            return (
                parseFloat(item.currentstockcount) + parseFloat(item.spareStock)
            );
        },
        fetchProfileUnitOptions() {
            profileUnitApi
                .fetchAll()
                .then((response) => {
                    if (!response.data.data) {
                        throw new Error('No data found');
                    }

                    const data = response.data.data;

                    this.profileUnitOptions = data.map((unit) => ({
                        id: parseInt(unit.profileUnitId),
                        name: unit.name,
                    }));
                })
                .catch((error) => {
                    console.error(error);
                });
        },
        updateProfileUnit(item, value) {
            item.unitRef = value;

            profileApi
                .updateProfileUnit(item.profileId, value)
                .then((response) => {
                    //
                })
                .catch((error) => {
                    console.error(error);
                });
        },
    },
    mounted() {
        this.fetchProfileUnitOptions();

        // show correct overview
        if (this.currentStockcountOverview == 'stockcount') {
            this.switchStockcountButtonLabel = this.trans(
                'pages.stockcount.buttons.standard_overview'
            );
        }

        if (this.showStockcountPhotos) {
            this.switchStockcountPhotosButtonLabel = this.trans(
                'pages.stockcount.buttons.hide_photos'
            );
        } else {
            this.switchStockcountPhotosButtonLabel = this.trans(
                'pages.stockcount.buttons.show_photos'
            );
        }

        profileApi.fetchCurrentStockCount().then((response) => {
            const startDate = response.data.data.startDate;
            const date = new Date(startDate * 1000);
            // days part from the timestamp
            const days = date.getDate();
            // month part from the timestamp
            const month = date.getMonth() + 1;
            // year part from the timestamp
            const year = date.getFullYear();

            const stockCountDate = days + '-' + month + '-' + year;
            this.$store.commit(
                'general/setPageTitle',
                this.trans('pages.stockcount.page_title', {
                    date: stockCountDate,
                }),
                { root: true }
            );
        });

        this.populateStockCountFilters();

        workstationApi.fetchAllWorkstations().then((response) => {
            this.workstationOptions = [
                {
                    value: null,
                    label: this.trans(
                        'pages.stockcount.options.choose_workstation'
                    ),
                },
            ];

            const workstations = [].concat.apply(
                [],
                Object.values(response.data.data)
            );

            this.workstationOptions = this.workstationOptions.concat(
                workstations.map((workstation) => ({
                    value: workstation.id,
                    label: workstation.label,
                }))
            );
        });

        this.$bus.$on('bottom-scroll', this.populateStockNextPage);
    },
    beforeRouteLeave(to, from, next) {
        this.$bus.$off('bottom-scroll', this.populateStockNextPage);
        next();
    },
};
</script>
